<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between flex-wrap text-white" style="padding: 32px 0; gap: 16px;">
            <div class="d-flex align-items-center" style="gap: 14px;">
              <router-link to="/quality-assurance" class="btn btn-sm btn-outline-light no-print" aria-label="Back to submissions">
                <i class="fe fe-arrow-left"></i>
              </router-link>
              <div class="header-icon-wrap">
                <i class="fe fe-award" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="page-title">{{ formName || "M&E Submission" }}</h4>
                <div class="d-flex flex-wrap align-items-center mt-1" style="gap: 8px;">
                  <span v-if="facilityName" class="header-id-badge">
                    <i class="fe fe-home mr-1"></i>{{ facilityName }}
                  </span>
                  <span v-if="sub.created_at" class="header-id-badge">
                    <i class="fe fe-calendar mr-1"></i>{{ sub.created_at | moment("D MMM YYYY, h:mm a") }}
                  </span>
                </div>
              </div>
            </div>
            <div class="d-flex align-items-center no-print" style="gap: 8px;">
              <download-excel
                :data="sub.responses"
                :fields="json_fields"
                class="btn btn-outline-light btn-sm"
                header="M and E Data"
                type="xls"
                :escapeCsv="false"
                :name="(facilityName || 'M_and_E') + ' M_and_E.xls'">
                <i class="fe fe-download mr-1"></i> Export
              </download-excel>
              <button class="btn btn-light btn-sm font-weight-bold" @click="printMe">
                <i class="fe fe-printer mr-1"></i> Print
              </button>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -40px;">
        <div class="container pb-5">

          <div v-if="loading" class="card shadow-sm p-4 text-muted">Loading the submission…</div>
          <div v-else-if="loadError" class="alert alert-danger">{{ loadError }}</div>

          <template v-else>
            <!-- ── Summary ── -->
            <div class="card shadow-sm summary-card mb-4">
              <div class="card-body px-4 py-4">
                <div class="row">
                  <div class="col-6 col-md-3 mb-3 mb-md-0">
                    <div class="vital-tile vital-tile--blue">
                      <i class="fe fe-check-square vital-icon text-primary"></i>
                      <div>
                        <p class="vital-value">{{ questions }}</p>
                        <p class="vital-label">Questions answered</p>
                      </div>
                    </div>
                  </div>
                  <div class="col-6 col-md-3 mb-3 mb-md-0">
                    <div class="vital-tile vital-tile--purple">
                      <i class="fe fe-layers vital-icon" style="color: #7c3aed;"></i>
                      <div>
                        <p class="vital-value">{{ groups.length }}</p>
                        <p class="vital-label">Sections</p>
                      </div>
                    </div>
                  </div>
                  <div class="col-12 col-md-6">
                    <div class="meta-grid">
                      <div class="info-row">
                        <i class="fe fe-user info-icon"></i>
                        <div>
                          <p class="info-label">Filled by</p>
                          <p class="info-value">{{ sub.name_of_contact_person || assessor || "—" }}</p>
                        </div>
                      </div>
                      <div class="info-row" v-if="enrolleeName">
                        <i class="fe fe-heart info-icon"></i>
                        <div>
                          <p class="info-label">Enrollee</p>
                          <p class="info-value">{{ enrolleeName }}</p>
                        </div>
                      </div>
                      <div class="info-row" v-if="sub.accreditation_category">
                        <i class="fe fe-tag info-icon"></i>
                        <div>
                          <p class="info-label">Category</p>
                          <p class="info-value">{{ sub.accreditation_category }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Find a question / jump to a section ── -->
            <div class="d-flex flex-wrap align-items-center mb-3 no-print" style="gap: 8px;" v-if="groups.length">
              <div class="search-wrap">
                <i class="fe fe-search"></i>
                <input v-model.trim="search" type="search" class="form-control form-control-sm" placeholder="Find a question or answer…"
                  aria-label="Find a question or answer" />
              </div>
              <a v-for="g in groups" :key="'jump-' + g.key" :href="'#cat-' + g.key" class="jump-chip">
                {{ g.name }} <span class="jump-count">{{ g.items.length }}</span>
              </a>
            </div>

            <!-- ── Responses by section ── -->
            <div v-for="g in shownGroups" :key="g.key" :id="'cat-' + g.key" class="card shadow-sm table-card mb-3 cat-card">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white">{{ g.name }}</h6>
                <span class="badge badge-light ml-auto" style="border-radius: 20px;">{{ g.items.length }}</span>
              </div>
              <div class="card-body px-4 py-2">
                <div v-for="(q, idx) in g.items" :key="q.key" class="q-row">
                  <span class="q-num">{{ idx + 1 }}</span>
                  <div class="flex-fill">
                    <div class="q-text">{{ q.question }}</div>
                    <div class="d-flex flex-wrap mt-2" style="gap: 6px;">
                      <span v-for="(a, i) in q.answers" :key="i" class="answer-chip" :class="answerClass(a)">{{ a }}</span>
                      <span v-if="!q.answers.length" class="text-muted small">No answer</span>
                    </div>
                    <div v-if="q.notes.length" class="small text-muted mt-1">
                      <i class="fe fe-message-square mr-1"></i>{{ q.notes.join(" · ") }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="search && !shownGroups.length" class="card shadow-sm p-4 text-center text-muted">
              No question or answer matches “{{ search }}”.
            </div>
            <div v-if="!groups.length" class="card shadow-sm p-5 text-center text-muted">
              <i class="fe fe-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
              <p class="mt-2 mb-0">No responses found</p>
            </div>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

export default {
  components: { Navbar },
  data() {
    return {
      loading: true,
      loadError: "",
      search: "",
      sub: { responses: [] },
      json_fields: {
        "Item Name": "item.item_name",
        Category: "item.category.category_name",
        Response: "response",
      },
    };
  },
  computed: {
    formName() {
      return this.sub.form ? this.sub.form.name : "";
    },
    facilityName() {
      return this.sub.provider ? this.sub.provider.agency_name : "";
    },
    assessor() {
      const t = this.sub.teamlead;
      return t ? [t.firstname, t.lastname].filter(Boolean).join(" ") || t.full_name : "";
    },
    enrolleeName() {
      const e = this.sub.enrollee;
      return e ? e.full_name || [e.firstname, e.lastname].filter(Boolean).join(" ") : "";
    },
    // One row per question (a multi-select question has one response per option), grouped by section in order.
    groups() {
      const groups = [];
      const byCat = {};
      (this.sub.responses || []).forEach((r) => {
        const item = r.item || {};
        const cat = item.category || {};
        const key = cat.id || "none";
        if (!byCat[key]) {
          byCat[key] = { key, name: cat.category_name || "Other questions", items: [], byItem: {} };
          groups.push(byCat[key]);
        }
        const g = byCat[key];
        const itemKey = r.quality_assurance_item_id || r.id;
        if (!g.byItem[itemKey]) {
          g.byItem[itemKey] = { key: itemKey, question: item.item_name || "Question", answers: [], notes: [] };
          g.items.push(g.byItem[itemKey]);
        }
        const q = g.byItem[itemKey];
        if (r.response !== null && r.response !== undefined && String(r.response).trim() !== "") q.answers.push(String(r.response));
        if (r.custom_input_value) q.notes.push(r.custom_input_value);
      });
      return groups;
    },
    questions() {
      return this.groups.reduce((n, g) => n + g.items.length, 0);
    },
    shownGroups() {
      const s = this.search.toLowerCase();
      if (!s) return this.groups;
      return this.groups
        .map((g) => ({
          ...g,
          items: g.items.filter((q) => q.question.toLowerCase().includes(s) || q.answers.some((a) => a.toLowerCase().includes(s))),
        }))
        .filter((g) => g.items.length);
    },
  },
  methods: {
    getSubmission() {
      this.axios
        .get(`/api/v1/auth/quality-assurances/${this.$route.params.id}`)
        .then((response) => {
          const data = response.data && response.data.data;
          if (!data) {
            this.loadError = "This submission was not found.";
            return;
          }
          this.sub = { ...data, responses: data.responses || [] };
        })
        .catch(() => { this.loadError = "Could not load the submission."; })
        .finally(() => { this.loading = false; });
    },
    answerClass(a) {
      const v = a.trim().toLowerCase();
      if (["yes", "available", "functional", "true"].includes(v)) return "answer-chip--yes";
      if (["no", "not available", "non functional", "non-functional", "false"].includes(v)) return "answer-chip--no";
      return "";
    },
    printMe() {
      this.search = "";
      this.$nextTick(() => window.print());
    },
  },
  created() {
    this.getSubmission();
  },
};
</script>

<style scoped>
.summary-card, .cat-card { border: none; border-radius: 12px; }
.meta-grid .info-row { padding: 6px 0; }
.meta-grid .info-row:last-child { border-bottom: none; }

.search-wrap { position: relative; flex: 1 1 240px; max-width: 320px; }
.search-wrap .fe { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.search-wrap input { padding-left: 30px; border-radius: 20px; }

.jump-chip {
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 20px;
  padding: 3px 12px; font-size: .8rem; color: #334155; text-decoration: none;
}
.jump-chip:hover { border-color: #2563eb; color: #2563eb; }
.jump-count { background: #f1f5f9; border-radius: 10px; padding: 0 6px; font-size: .72rem; color: #64748b; }

.q-row { display: flex; gap: 12px; padding: 14px 0; border-bottom: 1px solid #f1f5f9; }
.q-row:last-child { border-bottom: none; }
.q-num {
  display: inline-flex; width: 26px; height: 26px; border-radius: 50%;
  background: #eff6ff; color: #1d4ed8; font-size: .75rem; font-weight: 700;
  align-items: center; justify-content: center; flex-shrink: 0;
}
.q-text { font-size: .92rem; font-weight: 600; color: #1e293b; }

.answer-chip {
  display: inline-block; background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe;
  border-radius: 20px; padding: 2px 12px; font-size: .84rem; font-weight: 600;
}
.answer-chip--yes { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
.answer-chip--no { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }

@media print {
  .no-print, .sidebar-pinned { display: none !important; }
  .admin-main { margin-left: 0 !important; padding-top: 0 !important; }
  .page-header { background: none !important; padding-bottom: 0; }
  .page-header .text-white { color: #1e293b !important; }
  .header-id-badge { color: #1e293b !important; border: 1px solid #cbd5e1; background: none !important; }
  .cat-card { break-inside: avoid; box-shadow: none !important; border: 1px solid #e2e8f0; }
  .form-card-header { background: #f1f5f9 !important; }
  .form-card-header h6 { color: #1e293b !important; }
  section { margin-top: 0 !important; }
}
</style>
